import { DatePicker, Table, TableColumnsType } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import ErrorResponse from "../../../components/ErrorResponse";
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from "../../../components/Spinner";
import useAxios from "../../../hooks/useAxios";
import { ICampusCoupon, IGetCampusCoupons } from "../../../models/coupons.model";
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import Pagination from '../../../components/Pagination';
import config from '../../../lib/config';
import { useHistory, useLocation } from "react-router";
import QueryString from "qs";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../store/rootReducer";
import { getCampusCoupons } from "../../../api/coupons.api";
import _ from "lodash";
import SelectCampus from "../../../components/SelectCampus";

const PaymentCoupon: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetCampusCoupons>();

  const [data, setData] = useState<ICampusCoupon[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [param, setParam] = useState<any>({});

  useEffect(() => {
    execute(getCampusCoupons(currentCampus));
  }, [currentCampus]);

  useEffect(() => {
    if (res && !isLoading) {
      // query to state
      const _param: any = {};

      if (query.page) {
        setPage(Number(query.page));
        _param.page = Number(query.page);
      }
      if (query.startDate) {
        setStartDate(String(query.startDate));
        _param.startDate = String(query.startDate);
      }
      if (query.endDate) {
        setEndDate(String(query.endDate));
        _param.endDate = String(query.endDate);
      }
      if (query.keyword) {
        setKeyword(String(query.keyword));
        _param.keyword = String(query.keyword);
      }

      setParam(_param);

      // data filtering
      let _data = _.cloneDeep(res.data);

      if (query.startDate && query.endDate) {
        _data = _data.filter((item: any) => moment(item.createSrtDate).isAfter(String(query.startDate)));
        _data = _data.filter((item: any) => moment(item.createEndDate).isBefore(String(query.endDate)));
      }
      if (query.keyword) {
        _data = _data.filter((item: any) => item.couponName.indexOf(String(query.keyword)) !== -1);
      }

      setData(_data);
    }
  }, [res, isLoading, location]);

  const onSearch = () => {
    const _param: any = { page: 1 };

    if (startDate) _param.startDate = startDate;
    if (endDate) _param.endDate = endDate;
    if (keyword) _param.keyword = keyword;

    setParam(_param);
    history.push(`/payment/coupon?${QueryString.stringify(_param)}`);
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('payment.coupon.column1'),
      dataIndex: 'couponName',
      align: 'center',
      sorter: (a, b) => a.couponName?.localeCompare(b.couponName)
    },
    {
      title: t('payment.coupon.column2'),
      dataIndex: 'createSrtDate',
      align: 'center',
      sorter: (a, b) => a.createSrtDate?.localeCompare(b.createSrtDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD')
    },
    {
      title: t('payment.coupon.column3'),
      dataIndex: 'createEndDate',
      align: 'center',
      sorter: (a, b) => a.createEndDate?.localeCompare(b.createEndDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD')
    },
    {
      title: t('payment.coupon.column4'),
      dataIndex: 'validDays',
      align: 'center',
      sorter: (a, b) => a.validDays - b.validDays,
    },
    {
      title: t('payment.coupon.column5'),
      dataIndex: 'amount',
      align: 'center',
      sorter: (a, b) => a.amount - b.amount,
      render: (value: string) => value.toLocaleString()
    },
  ];

  return (
    <>
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('payment.coupon.column6')}</label>
            <div className="date-picker__area">
              <DatePicker
                value={startDate ? moment(startDate) : undefined}
                onChange={e => {
                  if (e === null) setStartDate('');
                  else setStartDate(moment(e).format('YYYY-MM-DD'))
                }}
              />
              ~
              <DatePicker
                value={endDate ? moment(endDate) : undefined}
                onChange={e => {
                  if (e === null) setEndDate('');
                  else setEndDate(moment(e).format('YYYY-MM-DD'))
                }}
              />
            </div>
          </li>
          <li>
            <label>{t('payment.coupon.placeholder1')}</label>
            <input
              type="text"
              className="form-control w-150p"
              placeholder={t('payment.coupon.placeholder1')}
              value={keyword}
              onChange={(e => setKeyword(e.target.value))}
              onKeyPress={e => { if (e.key === 'Enter') onSearch() }}
            />
            <div className="comm-btn__area">
              <Button
                className='btn-basic small black'
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <SimpleHeader
        iconFile="icon-payment-big.svg"
        parentMenu={t('common.menu.payment')}
        currentMenu={t('payment.coupon.title1')}
        onlyTitle={true}
      />

      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{data.length}</b>{t('common.text.unit')}
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.pageSize
            }}
            onRow={record => {
              return {
                onClick: () => {
                  history.push(`/payment/coupon/${currentCampus}/${record.couponCode}/list`);
                }
              };
            }}
            rowClassName="pointer"
          />
        </div>
        <div className="comm-bx__between">
          <div className="common-paging center">       
            <Pagination
              count={data.length}
              page={page}
              param={param}
              path="/payment/coupon"
            />
          </div>
        </div>
      </div>
      <Footer />
    </div>
    </>
  );
}

export default PaymentCoupon;
